<script setup lang="ts">
import { toast } from '@/helpers/toast';
import { isLoanAppComplete } from '@/helpers/typeGuards';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { useErhlCustomerLoanAppProcess } from '@/helpers/useErhlCustomerLoanAppProcess';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import router from '@/router';
import type { Applicant } from '@/types';
import { updateToken } from '@/api/backend/_http';
import { AppValues } from '@/helpers/const';
import { isMissingExistingProperty } from '@/helpers/erhlCustomerPropertyDetails';
import { ErhlCustomerProcessStep } from '@/types';

const { loanApp, send, saveAndGoNext, goBack } = useErhlCustomerLoanAppProcess();
const { isErhlD2cEnabled } = useFeatureFlags();

const appApi = useApplicationApi();
const isLoading = appApi.isLoading;

const saveAndContinue = () => {
  // Defence in depth: the route guard already requires the ERHL D2C flags, but a stale tab
  // could still reach this step after they are switched off. Block submission and send the
  // visitor back to the start.
  if (!isErhlD2cEnabled()) {
    router.push({ name: 'home' });
    return;
  }

  // Catches purchase applications that reached submit without an existing property (e.g. deep links)
  if (isMissingExistingProperty(loanApp.value)) {
    toast({
      type: 'danger',
      title: 'Error',
      description: 'You must add at least one existing property before submitting your application.',
    });
    send({ type: 'GOTO', step: ErhlCustomerProcessStep.PropertyDetails });
    return;
  }

  const primaryApplicant = loanApp.value.applicants.find((a) => a.type === 'primary');
  let applicants: Applicant[];
  if (primaryApplicant) {
    applicants = [primaryApplicant, ...loanApp.value.applicants.filter((a) => a.type !== 'primary')];
  } else {
    applicants = loanApp.value.applicants;
  }

  send({
    type: 'UPDATE',
    loanApp: {
      ...loanApp.value,
      applicants,
    },
  });

  submitApplication();
};

const submitApplication = async () => {
  const loanAppToSubmit = {
    ...loanApp.value,
    financials: { ...loanApp.value.financials },
  };

  if (!isLoanAppComplete(loanAppToSubmit)) {
    console.error('loan incomplete!', loanAppToSubmit);
    return toast({
      type: 'danger',
      title: 'Error',
      description: `Could not submit your application. It looks like something is missing in the application. Please contact ${AppValues.COMPANY_NAME} on ${AppValues.CONTACT} for support.`,
    });
  }

  const result = await appApi.submitApplication({ loanApp: loanAppToSubmit });
  if (!result.success) {
    if (['application-cancellation-error', 'applicant-already-exists-error'].includes(result.error)) {
      return toast({
        type: 'danger',
        title: 'Error',
        description: `Applicant's contact details are already in use. Please verify the information or use different contact details.`,
      });
    }
    const errMsg = `A system error has occurred. Please try again otherwise contact ${AppValues.COMPANY_NAME} on ${AppValues.CONTACT} for support`;
    return toast({ type: 'danger', title: 'Error', description: errMsg });
  }

  toast({ type: 'success', title: 'Application submitted successfully' });
  updateToken(null);
  saveAndGoNext();
};
</script>

<template>
  <div>
    <div class="w-full">
      <BxInfoPanel class="my-6" data-test-id="submission-disclaimer">
        You will be required to provide evidence of independent financial and legal advice, depending on your individual
        circumstances.
      </BxInfoPanel>

      <div class="mb-4 pb-2">
        <div>
          <h2 class="mr-1 inline text-lg font-medium">
            Please provide any additional information regarding your loan application
          </h2>
          <span class="text-xs">(optional)</span>
        </div>
        <div>
          <p>
            Sharing additional information can help speed up your application process, and it gives our team a better
            picture of your unique situation. If there's anything you'd like to add, particularly about your asset
            position, feel free to share in the space below.
          </p>
        </div>
        <BxTextarea
          id="submission-reason-for-fund"
          v-model="loanApp.summaryNotes"
          data-test-id="submission-reason-for-fund"
          class="mt-2 w-full"
          :rows="5"
        />
      </div>

      <div class="mt-12">
        <BxButton
          id="submission-submit"
          data-test-id="submission-submit"
          variant="primary"
          :loading="isLoading"
          @click="saveAndContinue()"
          >Submit application</BxButton
        >
        <BxButton variant="link" class="ml-4" data-test-id="submission-back" :disabled="isLoading" @click="goBack()">
          Back</BxButton
        >
      </div>
    </div>
  </div>
</template>
